import { Button, GenericFullscreenDialog, DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useState, useMemo, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useCreateInstallation } from '@/features/suppliers/hooks/installations'
import {
  InstallationFormData,
  validateInstallationForm
} from '../components/InstallationForm.schema'
import InstallationForm from '@/features/suppliers/pages/supplier-details/components/InstallationForm'
import { Partnership } from '@/features/suppliers/hooks/partnerships'

export default function InstallationCreateDialog({
  isOpen,
  close,
  partnershipId,
  supplierCountry,
  partnership
}: {
  isOpen: boolean
  close: () => void
  partnershipId: string
  supplierCountry: string
  partnership: Partnership | null
}) {
  const { t } = useI18n()

  const { mutateAsync: createInstallation, isPending: isSaving } =
    useCreateInstallation(partnershipId)

  const [formData, setFormData] = useState<InstallationFormData>({
    name: '',
    goodsFamily: '',
    ...DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
    usePartnershipAddress: true,
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    poBox: '',
    country: supplierCountry,
    unlocode: '',
    longitude: 0,
    latitude: 0
  })

  useEffect(() => {
    if (partnership) {
      setFormData(prev => ({
        ...prev,
        street: partnership.operatorStreet || '',
        streetNumber: partnership.operatorStreetNumber || '',
        street2: partnership.operatorStreet2 || '',
        postCode: partnership.operatorPostCode || '',
        city: partnership.operatorCity || '',
        poBox: partnership.operatorPoBox || ''
      }))
    }
  }, [partnership])

  const validationResult = useCallback(() => {
    return validateInstallationForm(formData)
  }, [formData])

  const isSaveDisabled = useMemo(() => {
    return isSaving || !validationResult().success
  }, [isSaving, validationResult])

  const onClose = useCallback(() => {
    setFormData({
      name: '',
      goodsFamily: '',
      ...DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
      usePartnershipAddress: true,
      street: partnership?.operatorStreet || '',
      streetNumber: partnership?.operatorStreetNumber || '',
      street2: partnership?.operatorStreet2 || '',
      postCode: partnership?.operatorPostCode || '',
      city: partnership?.operatorCity || '',
      poBox: partnership?.operatorPoBox || '',
      country: supplierCountry,
      unlocode: '',
      longitude: 0,
      latitude: 0
    })
    close()
  }, [supplierCountry, close, partnership])

  const onSave = useCallback(async () => {
    try {
      const validation = validationResult()

      if (!validation.success) {
        return
      }

      await createInstallation({
        name: validation.data.name,
        country: validation.data.country,
        goodsFamily: validation.data.goodsFamily,
        ...DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
        street: validation.data.street,
        street2: validation.data.street2,
        streetNumber: validation.data.streetNumber,
        postCode: validation.data.postCode,
        poBox: validation.data.poBox,
        city: validation.data.city,
        unlocode: validation.data.unlocode,
        longitude: validation.data.longitude,
        latitude: validation.data.latitude
      })

      toast.success(
        t('installations:createInstallationDialog.notifications.installationCreatedSuccessfully'),
        {
          position: 'top-right'
        }
      )
      onClose()
    } catch (error) {
      toast.error(
        t('installations:createInstallationDialog.notifications.failedToCreateInstallation'),
        {
          position: 'top-right'
        }
      )
      console.error(error)
    }
  }, [t, createInstallation, validationResult, onClose])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={onClose}
      title={t('installations:createInstallationDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('installations:createInstallationDialog.actions.add')}
          </Button>
        </div>
      }
    >
      <InstallationForm data={formData} onChange={setFormData} partnership={partnership ?? undefined} />
    </GenericFullscreenDialog>
  )
}
